import React from "react";

import { css } from "@emotion/react";

import { CSS_MQ } from "../../../styles/breakpoints";
import { Maybe } from "../../../utils/fp/small-fp-utils";
import { FONTS, fontSize } from "../../../styles/fonts";

import { capitalizeIfNoProductNameStyles } from "../../../styles/text-styles";
import { useTextWithProductNameRenderer } from "../../../domain/swarovski-optik/products/product-name/product-name-renderer";
import cn from "classnames";
import { withCapitalizationClassName } from "../../../domain/swarovski-optik/products/product-name/label-contains-product-name";

export const HeroImageHeadline: React.FC<{
  seoHeadline: Maybe<string>;
  emoHeadline: Maybe<string>;
  headlineStyle: "normal" | "bold";
  seoHeadlineStyle: "light" | "normal";
  isFirstHeroComponentOnPage: boolean;
}> = props => {
  const SeoHeadlineComponent = props.isFirstHeroComponentOnPage ? "h1" : "span";
  const EmoHeadlineComponent = props.isFirstHeroComponentOnPage ? "h2" : "span";

  const seoHeadline = useTextWithProductNameRenderer(props.seoHeadline || "");
  const emoHeadline = useTextWithProductNameRenderer(props.emoHeadline || "");

  return (
    <>
      {props.seoHeadline && (
        <SeoHeadlineComponent
          className={cn(props.seoHeadlineStyle, withCapitalizationClassName(seoHeadline.hasAnyProductName))}
          css={seoHeadlineStyles}
        >
          {seoHeadline.Element}
        </SeoHeadlineComponent>
      )}

      {props.emoHeadline && (
        <EmoHeadlineComponent
          className={cn(withCapitalizationClassName(emoHeadline.hasAnyProductName), "u-text-balance")}
          css={emoHeadlineStyles}
        >
          {emoHeadline.Element}
        </EmoHeadlineComponent>
      )}
    </>
  );
};

const seoHeadlineStyles = [
  FONTS.SwarovskiFutura500,
  fontSize.x3,
  css`
    margin: 0;

    &.light {
      font-weight: 300;

      ${capitalizeIfNoProductNameStyles};
    }

    &.normal {
      font-weight: 500;
    }

    color: inherit;

    & + :not(h2) {
      margin-top: 12px;
    }

    .bold > & {
      margin-top: 5px;
      margin-bottom: 10px;
    }
  `,
];

const emoHeadlineStyles = css`
  margin: 0;
  margin-left: auto;
  margin-right: auto;

  .bold > & {
    ${FONTS.SwarovskiFutura700};

    font-size: 16vw;
    letter-spacing: -0.0825em;
    line-height: 0.8;
    text-wrap: pretty;
    word-break: break-word;

    hyphens: auto;

    ${CSS_MQ.atLeastMd} {
      font-size: 110px;
    }

    ${CSS_MQ.atLeastLg} {
      font-size: 140px;
    }

    ${CSS_MQ.atLeastXl} {
      font-size: 160px;
    }

    order: -1;
    align-self: center;
  }

  .normal > & {
    font-family: MinervaModern;
    font-weight: 300;

    font-size: 30px;
    line-height: 1;

    ${CSS_MQ.atLeastMd} {
      font-size: 48px;
    }

    ${CSS_MQ.atLeastLg} {
      font-size: 60px;
    }

    margin-bottom: 20px;
  }

  text-transform: uppercase;

  color: inherit;

  max-width: 20em;
`;
